import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Shield, Zap, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './ClearSenseHero.css';

function ClearSenseHero() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <CheckCircle size={24} />,
      title: 'Logic Verification',
      description: 'Detect contradictions and inconsistencies instantly'
    },
    {
      icon: <Shield size={24} />,
      title: 'Claim Analysis',
      description: 'Extract and verify factual claims with sources'
    },
    {
      icon: <Zap size={24} />,
      title: 'Real-time Insights',
      description: 'Get instant feedback on AI-generated content'
    }
  ];

  return (
    <section className="clearsense-hero-section">
      <div className="clearsense-hero-container">
        {/* Background Elements */}
        <div className="clearsense-hero-bg">
          <div className="clearsense-gradient-orb clearsense-orb-1"></div>
          <div className="clearsense-gradient-orb clearsense-orb-2"></div>
          <div className="clearsense-gradient-orb clearsense-orb-3"></div>
        </div>

        {/* Content */}
        <motion.div
          className="clearsense-hero-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Badge */}
          <motion.div
            className="clearsense-badge"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Sparkles size={16} />
            <span>Introducing ClearSense</span>
          </motion.div>

          {/* Main Heading */}
          <motion.h2
            className="clearsense-hero-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Verify AI Answers.
            <br />
            <span className="clearsense-hero-gradient">Trust with Confidence.</span>
          </motion.h2>

          {/* Description */}
          <motion.p
            className="clearsense-hero-description"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            ClearSense is your AI answer sense-checker. Analyze responses for logical consistency,
            extract verifiable claims, and get instant insights. Free tier available with advanced
            Pro features for source verification.
          </motion.p>

          {/* Features Grid */}
          <motion.div
            className="clearsense-features-grid"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="clearsense-feature-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="clearsense-feature-icon">{feature.icon}</div>
                <h3 className="clearsense-feature-title">{feature.title}</h3>
                <p className="clearsense-feature-desc">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            className="clearsense-hero-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <motion.button
              className="clearsense-cta-primary"
              onClick={() => navigate('/clearsense')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Try ClearSense Free
              <ArrowRight size={20} />
            </motion.button>
            <motion.button
              className="clearsense-cta-secondary"
              onClick={() => navigate('/clearsense')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Learn More
            </motion.button>
          </motion.div>

          {/* Pricing Info */}
          <motion.div
            className="clearsense-pricing-info"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            <div className="clearsense-pricing-item">
              <span className="clearsense-pricing-label">Free Tier</span>
              <span className="clearsense-pricing-value">Logic checks, highlights, claims</span>
            </div>
            <div className="clearsense-pricing-divider"></div>
            <div className="clearsense-pricing-item">
              <span className="clearsense-pricing-label">Pro £19.99/mo</span>
              <span className="clearsense-pricing-value">Source verification, unlimited exports</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default ClearSenseHero;
